import React, { useEffect, useState } from 'react';
import { Box, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Paper, Snackbar, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, Typography } from '@mui/material';
import { useAppContext } from '../../hooks/AppContext';
import { getCompletedTasks, deleteTasks } from '../../services/GraphService/GraphService';
import moment from 'moment';
import Logger from '../../utils/Logger';
export const OldCompletedTasks = () => {
    var _a, _b;
    // State
    const app = useAppContext();
    const [tasks, setTasks] = useState([]);
    const [selectedTaskIds, setSelectedTaskIds] = useState([]);
    const [selectedTask, setSelectedTask] = useState(null);
    const [loading, setLoading] = useState(true);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [snackMessage, setSnackMessage] = useState('');
    const [snackOpen, setSnackOpen] = useState(false);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    // Load tasks on mount
    useEffect(() => {
        loadOldCompletedTasks();
    }, []);
    // Load and filter tasks
    const loadOldCompletedTasks = async () => {
        setLoading(true);
        try {
            const completedTasks = await getCompletedTasks(app.authProvider);
            const oldTasks = filterTasksOlderThanTwoYears(completedTasks);
            // Sort in ascending order (oldest first) by completedDateTime
            const sortedTasks = oldTasks.sort((a, b) => {
                var _a, _b;
                const dateA = moment((_a = a.todoTask.completedDateTime) === null || _a === void 0 ? void 0 : _a.dateTime);
                const dateB = moment((_b = b.todoTask.completedDateTime) === null || _b === void 0 ? void 0 : _b.dateTime);
                return dateA.diff(dateB, 'seconds');
            });
            setTasks(sortedTasks);
        }
        catch (error) {
            Logger.error(`Error loading old completed tasks: ${error}`);
            showSnack('Error loading tasks. Please try again.');
        }
        finally {
            setLoading(false);
        }
    };
    // Filter tasks completed more than 2 years ago
    const filterTasksOlderThanTwoYears = (tasks) => {
        const twoYearsAgo = moment().subtract(2, 'years');
        return tasks.filter(task => {
            var _a;
            const todoTask = task.todoTask;
            if ((_a = todoTask.completedDateTime) === null || _a === void 0 ? void 0 : _a.dateTime) {
                const completedDate = moment(todoTask.completedDateTime.dateTime);
                return completedDate.isBefore(twoYearsAgo);
            }
            return false;
        });
    };
    // Multi-select handlers
    const handleToggle = (taskId) => {
        setSelectedTaskIds(prev => {
            const currentIndex = prev.indexOf(taskId);
            if (currentIndex === -1) {
                return [...prev, taskId];
            }
            else {
                return prev.filter(id => id !== taskId);
            }
        });
    };
    const handleSelectAll = () => {
        if (selectedTaskIds.length === tasks.length) {
            setSelectedTaskIds([]);
        }
        else {
            setSelectedTaskIds(tasks.map(t => t.getId().taskId));
        }
    };
    const handleRowClick = (task) => {
        setSelectedTask(task);
    };
    // Delete handlers
    const handleDeleteClick = () => {
        if (selectedTaskIds.length === 0) {
            showSnack('Please select at least one task to delete');
            return;
        }
        setConfirmDialogOpen(true);
    };
    const handleConfirmDelete = async () => {
        setConfirmDialogOpen(false);
        setDeleting(true);
        try {
            const tasksToDelete = selectedTaskIds.map(id => {
                const task = tasks.find(t => t.getId().taskId === id);
                return task.getId();
            });
            const results = await deleteTasks(tasksToDelete, app.authProvider);
            // Remove successfully deleted tasks from state
            setTasks(prev => prev.filter(t => !results.success.some(s => s.taskId === t.getId().taskId)));
            // Clear selected task if it was deleted
            if (selectedTask && results.success.some(s => s.taskId === selectedTask.getId().taskId)) {
                setSelectedTask(null);
            }
            setSelectedTaskIds([]);
            if (results.failed.length > 0) {
                showSnack(`Deleted ${results.success.length} tasks. ${results.failed.length} failed.`);
            }
            else {
                showSnack(`Successfully deleted ${results.success.length} task(s)`);
            }
        }
        catch (error) {
            Logger.error(`Error deleting tasks: ${error}`);
            showSnack('Error deleting tasks. Please try again.');
        }
        finally {
            setDeleting(false);
        }
    };
    // Utility
    const showSnack = (message) => {
        setSnackMessage(message);
        setSnackOpen(true);
    };
    // Pagination
    const displayedTasks = tasks.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
    // Render loading state
    if (loading) {
        return (React.createElement(Box, { sx: { display: 'flex', justifyContent: 'center', p: 4 } },
            React.createElement(CircularProgress, null)));
    }
    // Render empty state
    if (tasks.length === 0) {
        return (React.createElement(Box, { sx: { p: 3, textAlign: 'center' } },
            React.createElement(Typography, { variant: "h6", color: "textSecondary" }, "No tasks completed more than 2 years ago")));
    }
    return (React.createElement(Box, { sx: { p: 2 } },
        React.createElement(Typography, { variant: "h4", gutterBottom: true }, "Old Completed Tasks"),
        React.createElement(Typography, { variant: "body2", color: "textSecondary", gutterBottom: true },
            "Tasks completed more than 2 years ago (",
            tasks.length,
            " total)"),
        React.createElement(Button, { variant: "contained", color: "error", onClick: handleDeleteClick, disabled: selectedTaskIds.length === 0 || deleting, sx: { mb: 2 } },
            "Delete Selected (",
            selectedTaskIds.length,
            ")"),
        React.createElement(Box, { sx: { display: 'flex', gap: 2, height: 'calc(100vh - 250px)' } },
            React.createElement(Box, { sx: { flex: '0 0 60%', overflow: 'auto' } },
                React.createElement(TableContainer, { component: Paper },
                    React.createElement(Table, { size: "small", stickyHeader: true },
                        React.createElement(TableHead, null,
                            React.createElement(TableRow, null,
                                React.createElement(TableCell, { padding: "checkbox" },
                                    React.createElement(Checkbox, { checked: selectedTaskIds.length === displayedTasks.length && displayedTasks.length > 0, indeterminate: selectedTaskIds.length > 0 && selectedTaskIds.length < displayedTasks.length, onChange: handleSelectAll })),
                                React.createElement(TableCell, null, "Title"),
                                React.createElement(TableCell, null, "Completed Date"),
                                React.createElement(TableCell, null, "List"))),
                        React.createElement(TableBody, null, displayedTasks.map((task) => {
                            var _a;
                            const taskId = task.getId().taskId;
                            const isSelected = selectedTaskIds.includes(taskId);
                            const completedDate = ((_a = task.todoTask.completedDateTime) === null || _a === void 0 ? void 0 : _a.dateTime)
                                ? moment(task.todoTask.completedDateTime.dateTime).format('M/D/YYYY')
                                : 'N/A';
                            return (React.createElement(TableRow, { key: taskId, selected: isSelected, hover: true },
                                React.createElement(TableCell, { padding: "checkbox" },
                                    React.createElement(Checkbox, { checked: isSelected, onChange: () => handleToggle(taskId) })),
                                React.createElement(TableCell, { onClick: () => handleRowClick(task), sx: { cursor: 'pointer' } }, task.getTitle()),
                                React.createElement(TableCell, null, completedDate),
                                React.createElement(TableCell, null, task.listName)));
                        })))),
                React.createElement(TablePagination, { component: "div", count: tasks.length, page: page, onPageChange: (e, newPage) => setPage(newPage), rowsPerPage: rowsPerPage, onRowsPerPageChange: (e) => {
                        setRowsPerPage(parseInt(e.target.value, 10));
                        setPage(0);
                    } })),
            React.createElement(Box, { sx: { flex: '0 0 40%', overflow: 'auto' } },
                React.createElement(Paper, { sx: { p: 2 } }, selectedTask ? (React.createElement(React.Fragment, null,
                    React.createElement(Typography, { variant: "h6", gutterBottom: true }, selectedTask.getTitle()),
                    React.createElement(Typography, { variant: "body2", color: "textSecondary", gutterBottom: true },
                        React.createElement("strong", null, "Status:"),
                        " ",
                        selectedTask.getStatus()),
                    React.createElement(Typography, { variant: "body2", color: "textSecondary", gutterBottom: true },
                        React.createElement("strong", null, "Completed:"),
                        ' ',
                        ((_a = selectedTask.todoTask.completedDateTime) === null || _a === void 0 ? void 0 : _a.dateTime)
                            ? moment(selectedTask.todoTask.completedDateTime.dateTime).format('MMMM D, YYYY')
                            : 'N/A'),
                    React.createElement(Typography, { variant: "body2", color: "textSecondary", gutterBottom: true },
                        React.createElement("strong", null, "List:"),
                        " ",
                        selectedTask.listName),
                    selectedTask.todoTask.categories && selectedTask.todoTask.categories.length > 0 && (React.createElement(Typography, { variant: "body2", color: "textSecondary", gutterBottom: true },
                        React.createElement("strong", null, "Categories:"),
                        " ",
                        selectedTask.todoTask.categories.join(', '))),
                    selectedTask.getBody() && (React.createElement(React.Fragment, null,
                        React.createElement(Typography, { variant: "subtitle2", sx: { mt: 2 } }, "Description:"),
                        React.createElement(Typography, { variant: "body2" }, (_b = selectedTask.getBody()) === null || _b === void 0 ? void 0 : _b.content))))) : (React.createElement(Typography, { variant: "body2", color: "textSecondary" }, "Click on a task to view details"))))),
        React.createElement(Dialog, { open: confirmDialogOpen, onClose: () => setConfirmDialogOpen(false) },
            React.createElement(DialogTitle, null, "Confirm Deletion"),
            React.createElement(DialogContent, null,
                React.createElement(Typography, null,
                    "Are you sure you want to delete ",
                    selectedTaskIds.length,
                    " task",
                    selectedTaskIds.length > 1 ? 's' : '',
                    "? This action cannot be undone.")),
            React.createElement(DialogActions, null,
                React.createElement(Button, { onClick: () => setConfirmDialogOpen(false) }, "Cancel"),
                React.createElement(Button, { onClick: handleConfirmDelete, color: "error", variant: "contained" }, "Delete"))),
        React.createElement(Snackbar, { open: snackOpen, autoHideDuration: 6000, onClose: () => setSnackOpen(false), message: snackMessage }),
        deleting && (React.createElement(Box, { sx: {
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                bgcolor: 'rgba(0, 0, 0, 0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 9999
            } },
            React.createElement(CircularProgress, null)))));
};
